import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ExternalLink } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import StatsBanner from "@/components/landing/StatsBanner";
import FinalCTA from "@/components/landing/FinalCTA";

const GINO_IMAGE =
  "https://customer-assets.emergentagent.com/job_7454cbe9-4bab-46e2-a1d4-503c4cbbe3d0/artifacts/pgvvgwg8_image.png";

const TIMELINE = [
  {
    year: "Sept. 2000",
    title: "El origen",
    text: "Gino Wickman, empresario de Detroit que había hecho el turnaround de su empresa familiar y la vendía con éxito, decide dedicarse tiempo completo a responder una pregunta: ¿qué hace que una empresa realmente funcione? Trabajando con más de 50 empresarios, empieza a desarrollar el modelo bajo el nombre Business Accelerator Model & Process.",
  },
  {
    year: "Junio 2006",
    title: "Nace EOS",
    text: "El modelo se rebautiza como Entrepreneurial Operating System® (EOS®). Un nombre que captura su propósito: un conjunto completo y práctico de principios y herramientas para que las empresas emprendedoras funcionen con visión, tracción y salud organizacional.",
  },
  {
    year: "Sept. 2007",
    title: "Traction — el libro fundacional",
    text: "Wickman publica \"Traction: Get a Grip on Your Business\", primero de forma independiente. El libro describe los Seis Componentes Clave de EOS, sus herramientas y el proceso de implementación. Supera los 2 millones de copias vendidas y ha sido traducido a más de diez idiomas.",
  },
  {
    year: "2008",
    title: "Nace EOS Worldwide",
    text: "Gino Wickman co-funda EOS Worldwide junto a Don Tinney. La organización se crea para entrenar y certificar a los Professional EOS Implementers® alrededor del mundo — los guías que acompañan a los equipos de liderazgo en el proceso de implementar la metodología.",
  },
  {
    year: "Hoy",
    title: "Un movimiento global",
    text: "EOS es hoy la metodología de gestión empresarial de más rápido crecimiento en el mundo de empresas medianas. Más de 33.000 empresas la han implementado, apoyadas por más de 900 implementadores certificados en decenas de países de América, Europa y Asia.",
  },
];

const EXTERNAL_LINKS = [
  {
    label: "EOS Worldwide",
    desc: "Información oficial, herramientas gratuitas y directorio de Implementadores.",
    href: "https://www.eosworldwide.com/",
    icon: ExternalLink,
  },
  {
    label: "Gino Wickman",
    desc: "Trayectoria personal, proyectos actuales y recursos del creador de EOS.",
    href: "https://www.ginowickman.com/",
    icon: ExternalLink,
  },
  {
    label: "Carlos Garcín",
    desc: "Ficha oficial como Professional EOS Implementer® en el directorio de EOS Worldwide.",
    href: "https://implementer.eosworldwide.com/carlos-garcin/",
    icon: ExternalLink,
  },
];

export default function HistoriaEOS() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "Gino Wickman y la Historia de EOS | Origen del Entrepreneurial Operating System",
      description: "Descubre cómo Gino Wickman creó EOS en el año 2000 y lo convirtió en la metodología que usan más de 34.000 empresas en el mundo. Carlos Garcín, Chile.",
      path: "/historia-eos",
      keywords: "historia EOS, Gino Wickman, origen Entrepreneurial Operating System, EOS Worldwide, Traction libro",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="Historia del Entrepreneurial Operating System"
          title={
            <>
              Cómo Gino Wickman creó{" "}
              <span className="text-[#F26522]">la metodología que cambió empresas</span>
            </>
          }
          subtitle="EOS nació de una pregunta simple y poderosa: ¿qué hace que una empresa realmente funcione? La respuesta tardó años en refinarse, pero hoy es la metodología de gestión de más rápido crecimiento en el mundo de empresas medianas."
          withCta
        />

        {/* Gino Wickman — foto + introducción */}
        <section data-testid="historia-origen" className="section-padding bg-white">
          <div className="container-eos">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">

              {/* Foto */}
              <div className="lg:col-span-4 reveal">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-2xl bg-[#F26522]/8 -z-10" />
                  <img
                    src={GINO_IMAGE}
                    alt="Gino Wickman, creador de EOS — Entrepreneurial Operating System"
                    className="w-full max-w-sm mx-auto lg:max-w-none rounded-2xl object-cover shadow-xl shadow-slate-200/60"
                    loading="lazy"
                  />
                  <div className="mt-4 text-center lg:text-left">
                    <p className="font-display font-extrabold text-navy-800 text-lg">Gino Wickman</p>
                    <p className="text-sm text-slate-500 mt-0.5">Fundador de EOS Worldwide · Autor de Traction</p>
                    <a
                      href="https://www.ginowickman.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="gino-website-link"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#F26522] hover:text-[#F26522]/80 font-medium transition-colors"
                    >
                      ginowickman.com <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Texto introductorio */}
              <div className="lg:col-span-8 space-y-6 reveal" style={{ transitionDelay: "100ms" }}>
                <span className="eyebrow">El origen</span>
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                  Un empresario que hizo la pregunta correcta.{" "}
                  <span className="text-[#F26522]">Y dedicó su vida a responderla.</span>
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Gino Wickman es un empresario nacido en Detroit que, tras haber hecho el turnaround de la empresa familiar y venderla con éxito, se hizo una pregunta que muy pocos se atreven a profundizar: <em className="not-italic font-semibold text-navy-800">"¿qué hace que una empresa realmente funcione?"</em>
                </p>
                <p className="text-slate-600 leading-relaxed">
                  No buscaba una teoría académica. Quería respuestas prácticas, aplicables desde el primer día por empresarios reales con empresas reales. Trabajando durante años con más de 50 empresarios, fue destilando principios atemporales y convirtiéndolos en un conjunto de herramientas simples y efectivas.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  El resultado fue el <strong className="text-navy-800">Entrepreneurial Operating System® (EOS®)</strong> — una metodología que no se limita a definir objetivos ni a optimizar procesos aislados, sino que fortalece simultáneamente los seis componentes clave de cualquier empresa: Visión, Personas, Datos, Asuntos, Procesos y Tracción.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline histórico */}
        <section data-testid="historia-timeline" className="section-padding bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">Línea de tiempo</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                De una pregunta en Detroit{" "}
                <span className="text-[#F26522]">a 33.000 empresas en el mundo</span>
              </h2>
            </div>

            <ol className="mt-12 relative ml-4 space-y-0">
              {/* Vertical line */}
              <span className="absolute left-0 top-0 bottom-0 w-px bg-slate-200" aria-hidden="true" />

              {TIMELINE.map((item, i) => (
                <li
                  key={i}
                  data-testid={`timeline-historia-${i + 1}`}
                  className="reveal pl-8 pb-10 relative"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  {/* Dot */}
                  <span className="absolute -left-[9px] top-1 w-[18px] h-[18px] rounded-full bg-[#F26522] ring-[3px] ring-white shadow-sm" />
                  <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                    {item.year}
                  </span>
                  <h3 className="mt-1 font-display font-extrabold text-xl text-navy-800">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Stats EOS hoy */}
        <StatsBanner />

        {/* EOS en Chile — Carlos Garcín */}
        <section data-testid="historia-chile" className="section-padding bg-white">
          <div className="container-eos">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
              <div className="reveal">
                <span className="eyebrow">EOS en Chile y Latinoamérica</span>
                <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                  Carlos Garcín, Implementador EOS oficial{" "}
                  <span className="text-[#F26522]">para Chile y Latinoamérica</span>
                </h2>
              </div>
              <div className="reveal space-y-5 text-slate-600 leading-relaxed" style={{ transitionDelay: "100ms" }}>
                <p>
                  Carlos Garcín es Professional EOS Implementer® certificado por EOS Worldwide, con base en Chile y con alcance a toda Latinoamérica. Su perfil es el del mundo operativo — de los que hacen que las cosas pasen. Esa misma energía de ejecución es la que trae a cada implementación.
                </p>
                <p>
                  Su misión es acompañar a empresarios en el proceso de implementar EOS de forma pura, sin atajos, para sacar lo mejor de su negocio y de su equipo de liderazgo. Sesiones presenciales y virtuales disponibles para empresas en Chile, Perú, Colombia, Argentina y toda la región.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to="/contacto"
                    data-testid="historia-agenda-btn"
                    className="btn-primary"
                  >
                    Agenda una llamada
                  </Link>
                  <a
                    href={`https://wa.me/56999176009?text=${encodeURIComponent("Hola Carlos, me gustaría conversar sobre EOS para mi empresa.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="historia-whatsapp-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-200 bg-white text-navy-800 font-semibold text-sm hover:border-[#F26522]/40 hover:shadow-md transition-all"
                  >
                    Contáctame por WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Links externos */}
        <section data-testid="historia-links" className="py-12 bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-xl reveal">
              <span className="eyebrow">Más información</span>
              <h2 className="mt-2 font-display font-extrabold text-2xl text-navy-800">
                Recursos oficiales de EOS
              </h2>
            </div>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
              {EXTERNAL_LINKS.map((l, i) => (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`external-link-${i + 1}`}
                  className="reveal group flex items-start gap-4 p-5 rounded-xl border border-slate-200 bg-white hover:border-[#F26522]/40 hover:shadow-md transition-all"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-9 h-9 rounded-lg bg-[#F26522]/10 flex items-center justify-center shrink-0 group-hover:bg-[#F26522] transition-colors">
                    <l.icon className="w-4 h-4 text-[#F26522] group-hover:text-white transition-colors" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-display font-bold text-navy-800">{l.label}</p>
                    <p className="text-sm text-slate-500 mt-0.5 leading-snug">{l.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
